<template>
  <div id="app" class="" :style="styleVars">
    <div class="header">
      <div class="container mx-auto px-3">
        <div class="header__logo">
          <img
            v-if="$config.header.logo"
            :src="$config.header.logo"
            :style="headerLogoStyle"
          />
        </div>
        <div class="header__title">
          <h1
            class="text-4xl font-bold py-6 md:py-0 mb-5"
            :style="headerTitleStyle"
          >
            {{ $config.header.text[lang.code] || $config.header.text["en"] }}
          </h1>
        </div>
        <div class="header__company-logo">
          <img
            v-if="$config.header.companyLogo"
            :src="$config.header.companyLogo"
            :style="headerCompanyLogoStyle"
          />
        </div>
      </div>
    </div>
    <transition name="fade">
      <v-loader v-if="isLoading" />
    </transition>
    <transition name="fade">
      <div
        v-if="!isLoading && !isValidFavourites"
        class="text-center mb-6 flex items-center justify-center"
        style="height:30vh;"
      >
        No properties to display.
      </div>
    </transition>
    <transition name="fade">
      <v-favourites
        style="flex-grow: 1;"
        v-if="!isLoading && isValidFavourites"
      />
    </transition>
    <v-footer />
    <transition name="fade">
      <lead-form-modal
        v-if="
          !isLoading &&
            isValidFavourites &&
            !isReturningUser &&
            isLeadFormTimeoutFinished &&
            !isLeadFormSubmitted
        "
        @submitted="isLeadFormSubmitted = true"
      />
    </transition>
  </div>
</template>

<script>
import LeadFormModal from "./components/LeadFormModal";
// import loadConfig from '../config'
import VLoader from "./components/VLoader";
import VFavourites from "./components/VFavourites";
import VFooter from "./components/VFooter";
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  name: "App",
  components: {
    LeadFormModal,
    VLoader,
    VFavourites,
    VFooter,
  },

  data() {
    return {
      isLeadFormSubmitted: false,
    };
  },

  computed: {
    ...mapState(["lang", "langs"]),
    ...mapGetters([
      "isLeadFormTimeoutFinished",
      "isLoading",
      "isReturningUser",
      "isValidFavourites",
    ]),

    styleVars() {
      const style = this.$config.style || {};
      const primaryFont = style.font || "Nexa";
      const secondaryFont = style.secondaryFont || primaryFont;
      const secondaryButtonFont = style.secondaryFont || "Nexa";

      return {
        "--brand-color": style.primaryColor,
        "--text-color": style.textColor,
        "--background-gradient": style.backgroundGradient || style.primaryColor,
        "--primary-font": this.fontStack(primaryFont, "Nexa"),
        "--secondary-font": this.fontStack(secondaryFont, primaryFont, "Nexa"),
        "--secondary-button-font": this.fontStack(secondaryButtonFont, "Nexa"),
        "font-family": "var(--primary-font)",
      };
    },

    headerLogoStyle() {
      return this.logoStyle(
        this.$config.header.logoMaxWidth,
        this.$config.header.logoMaxHeight,
      );
    },

    headerCompanyLogoStyle() {
      return this.logoStyle(
        this.$config.header.companyLogoMaxWidth,
        this.$config.header.companyLogoMaxHeight,
      );
    },

    headerTitleStyle() {
      const style = {
        color: this.$config.header.color,
      };

      if (this.$config.header.titleFontSize) {
        style.fontSize = this.$config.header.titleFontSize;
      }

      return style;
    },
  },

  created() {
    this.loadSecondaryFontStylesheet();
    this.fetchProperties();
  },

  methods: {
    ...mapActions(["fetchProperties"]),

    logoStyle(maxWidth, maxHeight) {
      const style = {};

      if (maxWidth) {
        style.maxWidth = maxWidth;
      }

      if (maxHeight) {
        style.maxHeight = maxHeight;
      }

      return style;
    },

    // font fallbacks. secondary -> primary -> Nexa -> system-ui -> sans-serif
    fontStack(...fonts) {
      const uniqueFonts = [];

      fonts.filter(Boolean).forEach((font) => {
        const isDuplicate = uniqueFonts.some(
          (uniqueFont) => uniqueFont.toLowerCase() === font.toLowerCase(),
        );

        if (!isDuplicate) {
          uniqueFonts.push(font);
        }
      });

      return uniqueFonts
        .map((font) => `"${font.replace(/"/g, '\\"')}"`)
        .concat(["system-ui", "sans-serif"])
        .join(", ");
    },

    // allows loading stylesheet directly into project config
    loadSecondaryFontStylesheet() {
      const stylesheet =
        this.$config.style && this.$config.style.secondaryFontStylesheet;

      if (!stylesheet || typeof document === "undefined") {
        return;
      }

      const isLoaded = Array.from(
        document.querySelectorAll("link[data-secondary-font-stylesheet]"),
      ).some((link) => link.getAttribute("href") === stylesheet);

      if (isLoaded) {
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = stylesheet;
      link.setAttribute("data-secondary-font-stylesheet", "");
      document.head.appendChild(link);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/_fonts";
@import "@/assets/styles/_variables";

body {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // background-color: $navy;
  color: var(--text-color);
  font-family: $font-sans;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.font-serif {
  font-family: $font-serif;
}

.header {
  background: var(--background-gradient);
  color: var(--text-color);
  margin-bottom: 30px;
  padding: 30px 0;

  > div {
    // text-align: center;
    align-items: center;
    display: grid;
    grid-template-columns: 1fr auto 1fr;

    img {
      max-height: 100px;
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
      justify-items: center;
      text-align: center;

      img {
        margin-bottom: 20px;
      }
    }
  }

  &__logo {
    justify-self: start;

    @media (max-width: 600px) {
      justify-self: center;
    }
  }

  &__title {
    align-self: stretch;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    text-align: center;

    h1 {
      margin-bottom: 0;
    }
  }

  &__company-logo {
    justify-self: end;

    @media (max-width: 600px) {
      justify-self: center;
    }
  }

  img {
    object-fit: contain;
    max-width: 100px;
    max-height: 50px;

    @media (min-width: 768px) {
      width: 180px;
    }
  }
}
</style>
